import { Autocomplete, AutocompleteProps, CircularProgress, TextField, TextFieldVariants } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../store/hooks";
import { ErrorText } from "./formBuilder/formBuilder.entity";
import { APICallFunction } from "../pages/userMangement/role/apiCall";
import { ResponseObject } from "../common/common.entity";

interface ISearchSelect extends AutocompleteProps<Record<string, (string | number)>, boolean, boolean, undefined> {
    /**
     * `width` of the autocomplete component
     */
    width?: string | number
    /**
     * Defines the key in `value` and `option` object to get the value
     * 
     * **Default** : `'id'`
     */
    valueExpr?: string | number
    /**
     * Defines the key in each `option` object to get the label of options
     * 
     * **Default** : `'Name'`
     */
    displayExpr?: string
    /**
     * `label` for input component
     */
    label?: string | null | undefined
    /**
     * `name` for input component
     */
    name?: string
    /**
     * `variant` for input component
     * 
     * **Syntax** : `<outlined | standard | filled>`
     * 
     * **Default** : `'outline'`
     */
    variant?: TextFieldVariants
    /**
     * error text to display in case of errors
     */
    error?: string
    /**
     * This function runs on component mount and should return options data
     * 
     * @param key
     * (optional) reducer key for the data returned
     * 
     * @param req 
     * (optional) request or params for api calls
     * 
     * @returns
     * `options` data array
     */
    getListData?: APICallFunction
    /**
     * reducer key to access data from redux store
     */
    apiKey?: string
    /**
     * Message to display in case of errors. String value for `helperText` in case of errors.
     * 
     * @type `<string>`
     * @default 'This field is required'
     */
    helperText?: string
    required?: boolean
    placeholder?: string
}

export const MuiSearchSelect = (props: Partial<ISearchSelect>) => {
    const crud = useAppSelector(state => state.crud);

    let {
        name, label, width, value = null,
        helperText, onChange, getListData,
        variant = 'outlined',
        valueExpr = 'id',
        displayExpr = 'Name',
        error,
        placeholder = "Search...",
        size = 'small',
        apiKey = (`${name}_List`),
        options = (crud[apiKey]?.data || []),
        required = false,
        ...others
    } = props;

    const loadingKey = `isLoading${apiKey}`;
    const selected = useMemo<ResponseObject | null>(() => {
        const val = options?.find((el: ResponseObject) => el[valueExpr] === value);
        return val || null;
    }, [value, options])

    useEffect(() => {
        if (getListData && value && !crud[apiKey]?.data) getListData({ key: apiKey });
    }, [value])

    const onOpen = async (e: any) => {
        if (!crud[apiKey]?.data && getListData) await getListData({ key: apiKey });
    }

    return (
        <Autocomplete
            size={size}
            id="multiple-limit-tags"
            options={options}
            getOptionLabel={(option) => option && `${option[displayExpr] || ''}`}
            sx={{ width: width }}
            // isOptionEqualToValue={(option, val) => option[valueExpr] === val[valueExpr]}
            getOptionKey={(option: any) => option?.id}
            {...others}
            value={selected}
            onOpen={onOpen}
            loading={crud[loadingKey] === 'loading'}
            onChange={onChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant={variant}
                    name={name}
                    label={label}
                    placeholder={placeholder}
                    required={required}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: [crud[loadingKey] === 'loading' ? <CircularProgress color="inherit" size={20} /> : null, params.InputProps.endAdornment],
                        },
                        formHelperText: {
                            sx: { color: 'gray' }
                        }
                    }}
                    {...(error && { error: true, helperText: error || ErrorText.required })}
                />
            )}
        />
    );
};
